import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Input, Modal, Select, Switch, TimePicker } from "antd";
import { Country, State } from "country-state-city";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import apiClient from "../../helpers/apiClient";
import customizeRequiredMark from "../../components/custom_antd/CustomizeRequiredMark";
import { useToast } from "../../contexts/ToastContext";
import { useCategoriesContext } from "../../contexts/CategoriesContext";
import ErrorModal from "../settings/components/integration/insurance/ErrorModal";
import {
    buildTimeZoneSelectOptions,
    operationalHoursToPickerValue,
    pickerValueToOperationalHours,
    suggestTimeZoneForState,
} from "../../helpers/operationalHours";

const FALLBACK_COVERAGE_STATES = [
    { abbreviation: "PA", fullName: "Pennsylvania" },
    { abbreviation: "CO", fullName: "Colorado" },
    { abbreviation: "CT", fullName: "Connecticut" },
    { abbreviation: "NV", fullName: "Nevada" },
    { abbreviation: "SC", fullName: "South Carolina" },
    { abbreviation: "MD", fullName: "Maryland" },
    { abbreviation: "GA", fullName: "Georgia" },
    { abbreviation: "CA", fullName: "California" },
    { abbreviation: "TX", fullName: "Texas" },
    { abbreviation: "AZ", fullName: "Arizona" },
    { abbreviation: "FL", fullName: "Florida" },
    { abbreviation: "HI", fullName: "Hawaii" },
    { abbreviation: "IL", fullName: "Illinois" },
    { abbreviation: "IN", fullName: "Indiana" },
    { abbreviation: "MO", fullName: "Missouri" },
    { abbreviation: "OR", fullName: "Oregon" },
    { abbreviation: "TN", fullName: "Tennessee" },
];

const LocationFormModal = ({ open, location = null, onCancel, onSuccess }) => {
    const { t } = useTranslation("cars");
    const [form] = Form.useForm();
    const toast = useToast();
    const { addLocation, updateLocation } = useCategoriesContext();

    const isEditMode = Boolean(location?.locationId);

    const [loading, setLoading] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [coverageStates, setCoverageStates] = useState([]);
    const [processingStates, setProcessingStates] = useState(false);
    const [states, setStates] = useState([]);

    const watchedCountry = Form.useWatch("country", form);
    const watchedTimeZone = Form.useWatch("timezone", form);
    const selectedCountryIso = useMemo(() => {
        if (!watchedCountry) return "";
        const found = Country.getAllCountries().find(
            (c) => c.name === watchedCountry || c.isoCode === watchedCountry,
        );
        return found?.isoCode || "";
    }, [watchedCountry]);

    const resolveCompanyCountry = async () => {
        const response = await apiClient.get("/api/company/my-company");
        const countryFromProfile = response?.data?.data?.address?.country;
        if (!countryFromProfile) {
            setErrorModalOpen(true);
            return null;
        }

        const matchCountry = Country.getAllCountries().find(
            (country) => country.name === countryFromProfile || country.isoCode === countryFromProfile,
        );
        return matchCountry?.name || countryFromProfile;
    };

    useEffect(() => {
        if (!open) return undefined;

        let cancelled = false;

        const initForm = async () => {
            form.resetFields();

            if (isEditMode) {
                form.setFieldsValue({
                    address: location.address,
                    city: location.city,
                    state: location.state,
                    country: location.country,
                    timezone: location.timezone || suggestTimeZoneForState(location.state),
                    overrideHours: Boolean(location.operationalHours?.enabled),
                    operationalHours: operationalHoursToPickerValue(location.operationalHours),
                });
                return;
            }

            try {
                const countryName = await resolveCompanyCountry();
                if (!cancelled && countryName) {
                    form.setFieldsValue({ country: countryName });
                }
            } catch (_error) {
                if (!cancelled) {
                    setErrorModalOpen(true);
                }
            }
        };

        initForm();

        return () => {
            cancelled = true;
        };
    }, [open, isEditMode, location, form]);

    useEffect(() => {
        if (!open) return undefined;

        const fetchCoverageStates = async () => {
            try {
                setProcessingStates(true);
                const response = await apiClient.get("/api/insurance/teqMobility-coverage-states");
                if (response?.data?.success && response?.data?.data?.states?.length) {
                    setCoverageStates(response.data.data.states);
                } else {
                    setCoverageStates(FALLBACK_COVERAGE_STATES);
                }
            } catch (_error) {
                setCoverageStates(FALLBACK_COVERAGE_STATES);
            } finally {
                setProcessingStates(false);
            }
        };

        fetchCoverageStates();
        return undefined;
    }, [open]);

    useEffect(() => {
        if (!selectedCountryIso) {
            setStates([]);
            return;
        }

        setProcessingStates(true);
        const filteredStates = State.getStatesOfCountry(selectedCountryIso).map((state) => {
            const result = coverageStates.find(
                (coveredState) => coveredState?.fullName?.toLowerCase() === state.name.toLowerCase(),
            );
            return {
                ...state,
                isInsuranceEnabled: !!result,
            };
        });
        setStates(filteredStates);
        setProcessingStates(false);
    }, [coverageStates, selectedCountryIso]);

    const handleClose = () => {
        form.resetFields();
        setLoading(false);
        onCancel?.();
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            // Minutes past midnight in THIS location's timezone, so a window means the same
            // thing at every branch no matter the offset between them.
            const hourMinutes = values.overrideHours ? pickerValueToOperationalHours(values.operationalHours) : null;

            const locationData = {
                address: values.address,
                city: values.city,
                state: values.state,
                country: values.country,
                timezone: values.timezone || suggestTimeZoneForState(values.state) || null,
                operationalHours: {
                    enabled: Boolean(values.overrideHours && hourMinutes),
                    startMinutes: hourMinutes?.startMinutes ?? null,
                    endMinutes: hourMinutes?.endMinutes ?? null,
                },
                isDeleted: false,
            };

            const response = isEditMode
                ? await apiClient.patch(`/api/location/${location.locationId}`, locationData)
                : await apiClient.post("/api/location/create", locationData);

            if (response?.data?.success) {
                if (isEditMode) {
                    updateLocation(location.locationId, locationData);
                    toast.success({
                        message: t("locations.successfullySavedChanges"),
                    });
                } else {
                    const created = response?.data?.data;
                    if (created) {
                        addLocation(created);
                    }
                    toast.success({
                        message: t("locations.successfullyAddedLocation"),
                    });
                }

                onSuccess?.(response?.data?.data || locationData);
                handleClose();
            }
        } catch (error) {
            const data = error?.response?.data;
            const detail =
                data?.errorSources?.map((source) => source?.message).filter(Boolean).join(" ") ||
                (data?.message && data.message !== "Validation Error" ? data.message : "");

            toast.error({
                message: isEditMode ? t("locations.failedToSave") : t("locations.errorAddingLocation"),
                description: detail || (isEditMode ? t("locations.errorSavingChanges") : undefined),
            });
        } finally {
            setLoading(false);
        }
    };

    const modalTitle = isEditMode ? t("locations.editLocation") : t("locations.createNewLocation");
    const submitLabel = isEditMode
        ? loading
            ? t("locations.saving")
            : t("locations.save")
        : loading
          ? t("locations.submitting")
          : t("locations.submit");

    return (
        <>
            <Modal
                open={open}
                onCancel={handleClose}
                title={<p className="text-lg font-semibold text-gray-900">{modalTitle}</p>}
                width={640}
                centered
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button shape="round" onClick={handleClose}>
                            {t("rates.cancel")}
                        </Button>
                        <Button
                            type="primary"
                            shape="round"
                            onClick={() => form.submit()}
                            loading={loading}
                            className="border-none bg-[#5d5dff] hover:bg-[#4e4ee8]">
                            {submitLabel}
                        </Button>
                    </div>
                }
                styles={{
                    body: { maxHeight: "min(72vh, 720px)", overflowY: "auto", paddingTop: 8 },
                }}>
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    requiredMark={customizeRequiredMark}
                    colon={false}>
                    <Form.Item
                        name="address"
                        label={t("locations.address")}
                        rules={[
                            {
                                required: true,
                                message: t("locations.pleaseInputAddress"),
                            },
                            {
                                whitespace: true,
                            },
                        ]}>
                        <Input className="rounded-full" />
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label={t("locations.city")}
                        rules={[
                            {
                                required: true,
                                message: t("locations.pleaseInputCity"),
                            },
                            {
                                whitespace: true,
                            },
                        ]}>
                        <Input className="rounded-full" />
                    </Form.Item>
                    <Form.Item
                        name="country"
                        label={t("locations.country")}
                        rules={[
                            {
                                required: true,
                                message: t("locations.pleaseInputCountry"),
                            },
                        ]}>
                        <Select
                            className="rounded-select"
                            placeholder={t("locations.pleaseInputCountry")}
                            disabled
                            showSearch
                        />
                    </Form.Item>
                    <Form.Item
                        name="state"
                        label={t("locations.state")}
                        rules={[
                            {
                                required: true,
                                message: t("locations.pleaseInputState"),
                            },
                            {
                                validator: (_, value) => {
                                    if (value && value.trim() === "") {
                                        return Promise.reject(t("locations.pleaseInputState"));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}>
                        <Select
                            className="rounded-select"
                            placeholder={t("locations.pleaseEnterStateName")}
                            loading={processingStates || selectedCountryIso.length === 0}
                            options={states.map((state) => ({
                                value: state.name,
                                label: state.isInsuranceEnabled ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <BsShieldFillCheck
                                            style={{ color: "#22c55e", fontSize: "0.9rem" }}
                                            title="Insurance enabled"
                                        />
                                        <span>{state.name}</span>
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <BsShieldFillExclamation
                                            style={{ color: "#f59e0b", fontSize: "0.9rem" }}
                                            title="Insurance not enabled"
                                        />
                                        <span>{state.name}</span>
                                    </div>
                                ),
                                searchLabel: state.name,
                            }))}
                            optionFilterProp="searchLabel"
                            filterOption={(input, option) => {
                                const searchText = typeof option?.searchLabel === "string" ? option.searchLabel : "";
                                const inputText = typeof input === "string" ? input : "";
                                return searchText.toLowerCase().includes(inputText.toLowerCase());
                            }}
                            showSearch
                        />
                    </Form.Item>

                    {/* The counter's own clock. Auto-suggested from the state, but explicit here
                        because split states (El Paso vs the rest of Texas) and any non-US branch
                        cannot be derived reliably — and a wrong zone silently shifts the window. */}
                    <Form.Item
                        name="timezone"
                        label={t("locations.timezone")}
                        tooltip={t("locations.timezoneDescription")}>
                        <Select
                            className="rounded-select"
                            placeholder={t("locations.timezonePlaceholder")}
                            options={buildTimeZoneSelectOptions(watchedTimeZone, {
                                us: t("locations.timezoneGroupUs"),
                                other: t("locations.timezoneGroupOther"),
                            })}
                            optionFilterProp="label"
                            allowClear
                            showSearch
                        />
                    </Form.Item>

                    {/* A branch only needs its own window when it differs from the company
                        default, so this is opt-in per location rather than a required field. */}
                    <Form.Item
                        name="overrideHours"
                        label={t("locations.overrideOperationalHours")}
                        tooltip={t("locations.overrideOperationalHoursDescription")}
                        valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) => prev.overrideHours !== next.overrideHours}>
                        {({ getFieldValue }) =>
                            getFieldValue("overrideHours") ? (
                                <Form.Item
                                    name="operationalHours"
                                    label={t("locations.operationalHours")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("locations.pleaseSetOperationalHours"),
                                        },
                                        {
                                            // Same opening and closing minute leaves one bookable
                                            // minute, which the API rejects. Catch it inline.
                                            validator: (_, value) => {
                                                const hours = pickerValueToOperationalHours(value);
                                                if (hours && hours.startMinutes === hours.endMinutes) {
                                                    return Promise.reject(
                                                        new Error(t("locations.operationalHoursSameTime")),
                                                    );
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}>
                                    <TimePicker.RangePicker
                                        format="hh:mm A"
                                        minuteStep={30}
                                        className="w-full rounded-full"
                                        getPopupContainer={() => document.body}
                                        changeOnScroll
                                    />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                </Form>
            </Modal>

            <ErrorModal
                open={errorModalOpen}
                handleClose={() => setErrorModalOpen(false)}
                title={"Fill up company address"}
                message={"Please fill up your company address in your company settings before creating a location"}
                url={"/settings?menu=companyInfo&edit=true"}
            />
        </>
    );
};

LocationFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    location: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

export default LocationFormModal;
